import { ComponentProps } from "react";
import { IComponentParent } from "sonobello.utilities.react";

import { ProtectedAppContextProvider } from "../../../AppContext";
import EnvironmentConfiguration from "../../../constants/EnvironmentConfiguration";
import NewPatientFlow from "../../Types/NewPatientFlow";
import NoBookFlow from "../../Types/NoBookFlow";
import RebookFlow from "../../Types/RebookFlow";
import ExperimentAssignmentAppContextProvider, {
  IExperimentAssignmentAppContextProviderProps
} from "../Components/ExperimentAssignmentAppContextProvider";
import useCreateCustomerExperimentManualAuth from "../Hooks/UseCreateCustomerExperiment";
import ExperimentKey from "../Types/ExperimentKey";
import FailoverFlowResolver from "../Types/FailoverFlowResolver";
import NameMatchFlowResolver from "../Types/NameMatchFlowResolver";
import NoConfirmationNewPatientFlow from "../Types/NoConfirmationNewPatientFlow";
import NoConfirmationNewPatientFlowResolver from "../Types/NoConfirmationNewPatientFlowResolver";
import NoConfirmationNoBookFlow from "../Types/NoConfirmationNoBookFlow";
import NoConfirmationNoBookFlowResolver from "../Types/NoConfirmationNoBookFlowResolver";
import NoConfirmationRebookFlow from "../Types/NoConfirmationRebookFlow";
import NoConfirmationRebookFlowResolver from "../Types/NoConfirmationRebookFlowResolver";
import NoWelcomeNoBookFlow from "../Types/NoWelcomeNoBookFlow";
import NoWelcomeRebookFlow from "../Types/NoWelcomeRebookFlow";
import ExperimentContextValueProvider from "./ExperimentContextValueProvider";

const confirmationPageRemovalKey =
  EnvironmentConfiguration.name === "Production"
    ? ExperimentKey.ConfirmationPageRemoval
    : ExperimentKey.ConfirmationPageRemovalDev;

const configuredExperimentAssignmentAppContextProvider: React.FC<
  IExperimentAssignmentAppContextProviderProps
> = props =>
  ExperimentAssignmentAppContextProvider({
    ...props,
    flowResolver: new FailoverFlowResolver([
      new NoConfirmationNewPatientFlowResolver(
        props.session.customer.isPreviouslyQualified,
        confirmationPageRemovalKey
      ),
      new NoConfirmationRebookFlowResolver(props.session.customer.isPreviouslyQualified, confirmationPageRemovalKey),
      new NoConfirmationNoBookFlowResolver(props.session.customer.isPreviouslyQualified, confirmationPageRemovalKey),
      new NameMatchFlowResolver([
        new NoConfirmationNewPatientFlow(props.session.customer.isPreviouslyQualified),
        new NoConfirmationRebookFlow(props.session.customer.isPreviouslyQualified),
        new NoConfirmationNoBookFlow(props.session.customer.isPreviouslyQualified),
        new NewPatientFlow(props.session.customer.isPreviouslyQualified),
        new RebookFlow(props.session.customer.isPreviouslyQualified),
        new NoBookFlow(props.session.customer.isPreviouslyQualified),
        new NoWelcomeNoBookFlow(props.session.customer.isPreviouslyQualified),
        new NoWelcomeRebookFlow(props.session.customer.isPreviouslyQualified)
      ])
    ]),
    useCreateCustomerExperiment: useCreateCustomerExperimentManualAuth,
    AppContext: ProtectedAppContextProvider
  });

const ExperimentContextAppContextProvider: React.FC<
  IComponentParent & Omit<IExperimentAssignmentAppContextProviderProps, "experimentContextProps">
> = ExperimentContextValueProvider<
  Pick<IExperimentAssignmentAppContextProviderProps, "experimentContextProps">,
  ComponentProps<typeof configuredExperimentAssignmentAppContextProvider>
>({
  Component: configuredExperimentAssignmentAppContextProvider,
  map: experimentContextProps => ({ experimentContextProps })
});

export default ExperimentContextAppContextProvider;
